import * as d3 from "d3";

import {
  ComboboxSelectionItem,
  DCNetscopeResourceWithName,
  DomainResolution,
  FlowDetail, FlowMetric,
  FlowRessourceObject,
  GraphDataObject
} from "@app/netscope/model/data-models";
import {
  FlowsRendererInterface
} from "@app/netscope/netscope-common-flowview/netscope-common-flowview.component";
import {FlowViewHost} from "@app/netscope/netscope-common-flowview/flowview-host";
import {NetscopeUtils} from "@app/netscope/netscope-utils";
import Graph from "graphology";
import {
  IPv4,
  IPv4CidrRange,
  IPv6, IPv6CidrRange,
  Pool,
  RangedSet, Validator
} from "ip-num";


let colors = {
  "focused": "#e79807",
  "infrastructure": "#007FCB",
  "external_ip": "#a0b3b3",
  "unknown_ip": "#a620e3",
  "internet_ip": "#d91919",
}
export default colors;

export class VisualisationDataForRenderers {
    links: FlowDetail[];
    vms: FlowRessourceObject[];
    hosts: FlowRessourceObject[];
    minBytes: number;
    maxBytes: number;
    totalTrafficBytes: number;
    focusedVmsUuids: string[];
    ignoredUuids: string[];
}

export class MergeLinkItem {
  clients: Map<string, FlowRessourceObject>;
  servers: Map<string, FlowRessourceObject>;
  flows: FlowDetail[];
}

export class ClustersViewerRenderer implements FlowsRendererInterface {

  flowComponent: FlowViewHost;

  groupNodes(nodes: FlowRessourceObject[], links: FlowDetail[], newNodes: FlowRessourceObject[], replacementMap: Map<string, FlowRessourceObject>) {
    let replacementFlowsMap: Map<string, FlowDetail> = new Map();
    let newLinks: FlowDetail[] = [];
    // let newFlows: FlowDetail[] = [];

    for (let flow of links) {
      if (! replacementMap.has(flow.source.uuid) && ! replacementMap.has(flow.target.uuid)) {
        newLinks.push(flow);
        continue
      }

      let source = flow.source;
      let target = flow.target;

      if (replacementMap.has(flow.source.uuid)) {
        source = replacementMap.get(flow.source.uuid);
      }
      if (replacementMap.has(flow.target.uuid)) {
        target = replacementMap.get(flow.target.uuid);
      }

      let flowKey = `${source.uuid}__${target.uuid}`;
      if (!replacementFlowsMap.has(flowKey)) {
        let newFlow = new FlowDetail();
        newFlow.source = source;
        newFlow.target = target;
        newFlow.metrics = new Map<number, FlowMetric>();
        newFlow.port = -1;
        newFlow.subflows = [];
        replacementFlowsMap.set(flowKey, newFlow);
        newLinks.push(newFlow);
      }

      let replacementFlow = replacementFlowsMap.get(flowKey);
      replacementFlow.subflows.push(flow);

      for (let [metricPort, metric] of flow.metrics) {

        if (! replacementFlow.metrics.has(metric.port)) {
          let newFlowMetric = new FlowMetric();
          newFlowMetric.port = metricPort;
          newFlowMetric.exchanged_bytes = 0;
          newFlowMetric.exchanged_packets = 0;
          newFlowMetric.direction = metric.direction;
          newFlowMetric.application_protocol = metric.application_protocol;
          newFlowMetric.transport_protocols = metric.transport_protocols;
          replacementFlow.metrics.set(metric.port, newFlowMetric);
        }

        // set exchanged_bytes
        let existingFlowMetric = replacementFlow.metrics.get(metric.port)
        existingFlowMetric.exchanged_bytes += metric.exchanged_bytes;
        existingFlowMetric.exchanged_packets += metric.exchanged_packets;

      }
      flow.shouldBeDeleted = true;
    }

    // Remove links that should be deleted
    links = links.filter((l) => !l.shouldBeDeleted);
    links = links.concat(newLinks);
    // links.push(...newLinks);

    // Add new grouped nodes
    nodes = nodes.filter((n) => true);
    nodes = nodes.concat(newNodes);
    // nodes.push(...newNodes);

    // List nodes that are in links
    let nodeUuidMap = new Map<string, boolean>();
    for (let link of links) {
      nodeUuidMap.set(link.source.uuid,  true);
      nodeUuidMap.set(link.target.uuid,  true);
    }

    // Filter nodes
    nodes = nodes.filter((n) => nodeUuidMap.has(n.uuid));

    return {
      nodes: nodes,
      links: links
    }
  }

  generateWhereClause = (flowComponent: FlowViewHost) => {
    // Filter links according to the top-menu of clusters-viewer
    let repositoryWhereParts: string[] = ["true"];
    if (!flowComponent.clusterViewerVisibleResourcesOptions.internet) {
      repositoryWhereParts.push("(not (src_resource_type = 'internet_ip' or dst_resource_type = 'internet_ip'))")
    }
    if (!flowComponent.clusterViewerVisibleResourcesOptions.infrastructure) {
      repositoryWhereParts.push("(not (src_resource_type = 'vm' or dst_resource_type = 'vm' or src_resource_type = 'host' or dst_resource_type = 'vm'))")
    }
    if (!flowComponent.clusterViewerVisibleResourcesOptions.external_ips) {
      repositoryWhereParts.push("(not (src_resource_type = 'external_ip' or dst_resource_type = 'external_ip'))")
    }
    if (!flowComponent.clusterViewerVisibleResourcesOptions.unknown_ips) {
      repositoryWhereParts.push("(not (src_resource_type = 'unknown_ip' or dst_resource_type = 'unknown_ip'))")
    }
    let whereClause = repositoryWhereParts.join(" and ");
    return whereClause;
  }

  computeVisualisationDataForRenderers(graphData: GraphDataObject, flowComponent: FlowViewHost): VisualisationDataForRenderers {
    console.log("computing 1");
    let minBytes = 0;
    let maxBytes = 0;

    let links = graphData.links;
    if (links.length > 0) {
      minBytes = NetscopeUtils.getMin(links.map((d) => d.exchanged_bytes));
      maxBytes = NetscopeUtils.getMax(links.map((d) => d.exchanged_bytes));
    }
    console.log("computing 2");

    // Filter links according to the top-menu of clusters-viewer
    let repositoryWhereParts: string[] = ["true"];
    if (!flowComponent.clusterViewerVisibleResourcesOptions.internet) {
      links = links.filter((link) => !((link.source.type === "internet_ip") || (link.target.type === "internet_ip")));
    }
    if (!flowComponent.clusterViewerVisibleResourcesOptions.infrastructure) {
      links = links.filter((link) => !(link.source.type === "vm" || link.target.type === "vm" || link.source.type === "host" || link.target.type === "host"));
    }
    if (!flowComponent.clusterViewerVisibleResourcesOptions.external_ips) {
      links = links.filter((link) => !(link.source.type === "external_ip" || link.target.type === "external_ip"));
    }
    if (!flowComponent.clusterViewerVisibleResourcesOptions.unknown_ips) {
      links = links.filter((link) => !((link.source.type === "unknown_ip") || (link.target.type === "unknown_ip")));
    }
    console.log("computing 3");

    // Filter links with a datacenter
    links = links.filter((l) => {
      if (l.source.type === 'datacenter' || l.target.type === 'datacenter:') {
        return false;
      }
      return true;
    });
    console.log("computing 4");

    let ignoredUuids = flowComponent.currentComboboxSelection
      .filter((selectionElement) => selectionElement.mode === "ignore")
      .map((selectionElement) => selectionElement.resource.uuid);

    // Find the elements that are part of the infrastructure
    let vms = graphData.nodes
      // .filter((vm) => vm.host !== undefined)
      .filter((vm) => !(vm.type === "internet_ip"))
      .filter((n) => ignoredUuids.indexOf(n.uuid) === -1);

    let replaceNodesMap: Map<string, FlowRessourceObject> = new Map<string, FlowRessourceObject>();
    console.log("computing 5");

    // If internet is enabled, put replacement items in the replaceNodesMap
    if (flowComponent.clusterViewerVisibleResourcesOptions.internet) {
      let internetUnknownIps = graphData.nodes
        .filter((vm) => vm.type === "internet_ip")
        .filter((vm) => vm.asnInfo?.asn != undefined)
        .filter((vm) => vm.asnInfo?.asn !== "Not routed");
      if (flowComponent.clusterViewerShowASNs === "asns") {
        let asnNodesMap: Map<string, FlowRessourceObject> = new Map<string, FlowRessourceObject>();
        for (let unknownIp of internetUnknownIps) {
          if (!asnNodesMap.has(unknownIp.asnInfo?.asn)) {
            let newNode = new FlowRessourceObject();
            newNode.name = unknownIp.asnInfo?.asn;
            newNode.uuid = `vim.asn:${unknownIp.asnInfo?.asn}`;
            newNode.type = "asn";
            newNode.address = unknownIp.asnInfo?.asn;
            asnNodesMap.set(unknownIp.asnInfo?.asn, newNode);
          }
          let replacementNode = asnNodesMap.get(unknownIp.asnInfo?.asn);
          replaceNodesMap.set(unknownIp.uuid, replacementNode);
        }

        // add ASNs nodes to vms list
        let asnsNodes = Array.from(asnNodesMap.values());
        vms.push(...asnsNodes);
      } else {
        // vms.push(...internetUnknownIps);
        vms = vms.concat(internetUnknownIps);
      }
    }
    console.log("computing 6");

    const vmsUuids = vms.map((vm) => vm.uuid);
    const vmsUuuidsMap: Map<string, boolean> = new Map<string, boolean>();
    for (let uuid of vmsUuids) {
      vmsUuuidsMap.set(uuid, true);
    }

    function isRelevant(uuid: string) {
      return vmsUuuidsMap.has(uuid);
    }

    let relevantExternalIpUuidsIdx = {};
    for (let link of graphData.links) {
      if (link.source.type === "external_ip" && relevantExternalIpUuidsIdx[link.source.uuid] === undefined) {
        relevantExternalIpUuidsIdx[link.source.uuid] = link.source.uuid;
      }
      if (link.target.type === "external_ip" && relevantExternalIpUuidsIdx[link.target.uuid] === undefined) {
        relevantExternalIpUuidsIdx[link.target.uuid] = link.target.uuid;
      }
    }
    console.log("computing 7");

    let focusedVmsUuids = flowComponent.currentComboboxSelection
      .filter((s) => s.mode === "focus")
      .map((s) => s.resource.uuid);
    let focusedAppsGroups = flowComponent.currentComboboxSelection
      .filter((s) => s.mode === "appgroup");
    for (let appGroup of focusedAppsGroups) {
      focusedVmsUuids.push(...appGroup.manualSqlUuids);
    }
    console.log("computing 8");

    // Detect hosts relevant to this visualisation, from the host property of VMs
    const hosts = graphData.nodes.filter((n) => n.type == "host")
      .filter((n) => ignoredUuids.indexOf(n.uuid) === -1);

    links = links.filter((l) => isRelevant(l.source.uuid) && isRelevant(l.target.uuid));
    console.log("computing 9");

    // If Internet resources are shown, replace the IPs with ASN that have been set in replaceNodesMap.
    if (flowComponent.clusterViewerVisibleResourcesOptions.internet) {
      let asnsNodes = vms.filter((vm) => vm.type === "asn");
      let newGraphData = this.groupNodes(vms, graphData.links, asnsNodes, replaceNodesMap);

      // nodes = newGraphData.nodes;
      links = newGraphData.links;
    }
    console.log("computing 10");

    // Compute total traffic, in order to help compute colors of links
    let totalTrafficBytes = 0;
    for (let link of links) {
      for (let metricTuple2 of link.metrics) {
        totalTrafficBytes += metricTuple2[1].exchanged_bytes;
      }
    }
    console.log("computing 10.5");

    if (focusedVmsUuids.length > 0 && flowComponent.showIndirectLinks === "no") {
      links = links.filter((l) => focusedVmsUuids.indexOf(l.source.uuid) !== -1 || focusedVmsUuids.indexOf(l.target.uuid) !== -1);
    }
    console.log("computing 11");

    flowComponent.filteredLinks = links;
    console.log("computing 12");

    // Ensure that between two hosts, there is at most one link that is displayed
    let linkIndex: Map<string, FlowDetail> = new Map();
    for (let link of links) {
      let key = `${link.source.uuid}__${link.target.uuid}`;
      if (!linkIndex.has(key)) {
        linkIndex.set(key, link)
      } else {
        let existingLink = linkIndex.get(key);
        existingLink.metrics = new Map([...existingLink.metrics.entries(), ...link.metrics])
      }
    }
    links = [...linkIndex.values()];
    console.log("computing 13");

    let nodeIndex: Map<string, FlowRessourceObject> = new Map<string, FlowRessourceObject>();

    for (let link of links) {
      for (let resource of [link.source, link.target]) {
        if (!nodeIndex.has(resource.uuid)) {
          nodeIndex.set(resource.uuid, resource);
        }
      }
    }
    let nodes = Array.from(nodeIndex.values());
    console.log("computing 14");

    // if (flowComponent.groupSimilarNodes) {
    //   // Compute a graph of the network and check if some optimization could be made
    //   const graph = new Graph();
    //
    //   for (let node of nodes) {
    //     graph.addNode(node.uuid, {object: node});
    //   }
    //   for (let link of links) {
    //     graph.addEdge(link.source.uuid, link.target.uuid, {object: link});
    //   }
    //
    //   // For each node, build a hash of its neighbours
    //   let neighboursHash: Map<string, string[]> = new Map();
    //   for (let node of graph.nodes()) {
    //     let neighbours = graph.neighbors(node).sort()
    //     let key = neighbours.join("/")
    //     if (! neighboursHash.has(key)) {
    //       neighboursHash.set(key, [])
    //     }
    //     neighboursHash.get(key).push(node);
    //   }
    //
    //   let newNodes: FlowRessourceObject[] = [];
    //
    //   let replacementMap = new Map<string, FlowRessourceObject>();
    //   let nextGroupId = 0
    //   for (let [_, similarNodesUuids] of neighboursHash) {
    //
    //     let resourcesTypes = new Set(similarNodesUuids.map((uuid) => nodeIndex.get(uuid).type));
    //
    //     for (let resourceType of resourcesTypes) {
    //       let similarNodesUuidsForThisResourceType = similarNodesUuids.filter((uuid) => nodeIndex.get(uuid).type == resourceType);
    //
    //       if (similarNodesUuidsForThisResourceType.length < 5) {
    //         continue;
    //       }
    //       let similarNodesIpaddresses = similarNodesUuidsForThisResourceType.map((uuid) => nodeIndex.get(uuid)).map((n) => n.address);
    //       let replacementNode = new FlowRessourceObject();
    //       replacementNode.name = `${similarNodesUuidsForThisResourceType.length} grouped ${resourceType}s`;
    //
    //       if (resourceType == "unknown_ip" || resourceType == "external_ip") {
    //         try {
    //           // <IP experiment>
    //           let arrays: RangedSet<IPv4 | IPv6>[] = new Array<RangedSet<IPv4 | IPv6>>();
    //           for (let ipaddress of similarNodesIpaddresses) {
    //             let [isIpV4, reasons] = Validator.isValidIPv4String(ipaddress);
    //             let ipObject : IPv4CidrRange | IPv6CidrRange = isIpV4 ? IPv4CidrRange.fromCidr(ipaddress+"/24") : IPv6CidrRange.fromCidr(ipaddress+"/64")
    //             arrays.push(RangedSet.fromCidrRange(ipObject));
    //           }
    //
    //           let pool = Pool.fromRangeSet(arrays);
    //           let aggregatedPool = pool.aggregate();
    //
    //           let rangesAsString = aggregatedPool.getRanges().map((r) => r.toCidrRange().toCidrString()).join(", ");
    //           replacementNode.name = `${similarNodesIpaddresses.length} IPs from ${rangesAsString}`;
    //           // </IP experiment>
    //         } catch (e) {
    //           console.warn("Could not compute a nice name for this group of IPs, I will keep the default one. Details about the execption:");
    //           console.warn(e);
    //         }
    //       }
    //
    //       replacementNode.uuid = `vim.group:group_${nextGroupId}`;
    //       replacementNode.type = resourceType;
    //       replacementNode.address = "0.0.0.0";
    //       for (let nodeUuid of similarNodesUuidsForThisResourceType) {
    //         replacementMap.set(nodeUuid, replacementNode);
    //       }
    //       replacementNode.subnodes = nodes.filter((n) => replacementMap.has(n.uuid));
    //       newNodes.push(replacementNode);
    //       nextGroupId += 1;
    //     }
    //   }
    //
    //   let alternativeGraph = this.groupNodes(nodes, links, newNodes, replacementMap);
    //
    //   nodes = alternativeGraph.nodes
    //   links = alternativeGraph.links
    // }

    return {
      links: links,
      vms: nodes,
      hosts: hosts,
      minBytes: minBytes,
      maxBytes: maxBytes,
      totalTrafficBytes: totalTrafficBytes,
      focusedVmsUuids: focusedVmsUuids,
      ignoredUuids: ignoredUuids
    };
  }

  render(flowComponent: FlowViewHost): void {
    this.flowComponent = flowComponent;
  }

  zoomInVisualisation = (flowComponent: FlowViewHost) => {
    console.log("zoom in visualisation");
    let transformation = {
      x: flowComponent.graphParameters.firstTransformation.x,
      y: flowComponent.graphParameters.firstTransformation.y,
      k: flowComponent.graphParameters.firstTransformation.k
    };

    if (flowComponent.graphParameters?.lastTransformation?.x !== undefined) {
      transformation.x = flowComponent.graphParameters.lastTransformation.x;
    }

    if (flowComponent.graphParameters?.lastTransformation?.y !== undefined) {
      transformation.y = flowComponent.graphParameters.lastTransformation.y;
    }

    if (flowComponent.graphParameters?.lastTransformation?.k !== undefined) {
      transformation.k = flowComponent.graphParameters.lastTransformation.k;
    }

    transformation.k *= 1.2;
    flowComponent.graphParameters.userMovedMap = true;

    flowComponent.graphParameters.lastTransformation = transformation;

    flowComponent.reloadUi();
  }

  zoomOutVisualisation = (flowComponent: FlowViewHost) => {
    console.log("zoom out visualisation");
    let transformation = {
      x: flowComponent.graphParameters.firstTransformation.x,
      y: flowComponent.graphParameters.firstTransformation.y,
      k: flowComponent.graphParameters.firstTransformation.k
    };

    if (flowComponent.graphParameters?.lastTransformation?.x !== undefined) {
      transformation.x = flowComponent.graphParameters.lastTransformation.x;
    }

    if (flowComponent.graphParameters?.lastTransformation?.y !== undefined) {
      transformation.y = flowComponent.graphParameters.lastTransformation.y;
    }

    if (flowComponent.graphParameters?.lastTransformation?.k !== undefined) {
      transformation.k = flowComponent.graphParameters.lastTransformation.k;
    }

    transformation.k *= 0.8;
    flowComponent.graphParameters.userMovedMap = true;

    flowComponent.graphParameters.lastTransformation = transformation;

    flowComponent.reloadUi();
  }

  recenterVisualisation = (flowComponent: FlowViewHost) => {
    console.log("recenter visualisation");
    flowComponent.graphParameters.userMovedMap = false;
    flowComponent.graphParameters.lastTransformation.x = flowComponent.graphParameters.firstTransformation.x;
    flowComponent.graphParameters.lastTransformation.y = flowComponent.graphParameters.firstTransformation.y;
    flowComponent.graphParameters.lastTransformation.k = flowComponent.graphParameters.firstTransformation.k;

    flowComponent.reloadUi();
  }

  destroy(flowComponent: FlowViewHost): void {}

  update(flowComponent: FlowViewHost): void {}

  isOutdated(): boolean {
    return this.flowComponent.selectedView !== "clusters-viewer";
  }
}

